import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { sidebarState, wrap } from '../features/sidebar/sidebarSlice';
import { logout, reset } from '../features/auth/authSlice';
import { reset as resetProfile } from '../features/profile/profileSlice';
import { toast } from 'react-toastify';
import { setPic } from '../features/pic/picSlice';
import { Icon } from '@iconify/react';
import logoSiberat from '../svg/logo.svg';

const navItems = [
  { to: '/dashboard', label: 'Dashboard', icon: 'solar:widget-2-bold' },
  { to: '/order', label: 'Order', icon: 'solar:box-bold' },
  { to: '/invoice', label: 'Invoice', icon: 'solar:document-text-bold' },
  { to: '/profile', label: 'Profile', icon: 'solar:user-bold' }
];

const MOBILE_MQ = '(max-width: 639px)';

const getPhotoFile = (...candidates) => {
  for (const value of candidates) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return null;
};

const Sidebar = () => {
  const open = useSelector(sidebarState);
  const { user } = useSelector(state => state.auth);
  const { profile, isSuccessUpload } = useSelector(state => state.profile);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;
  const splitLocation = pathname.split('/');
  const urlLogo = process.env.REACT_APP_LOGO_URL;

  const photoFile = useMemo(
    () => getPhotoFile(profile?.data?.Customer?.photo, user?.data?.foto),
    [profile?.data?.Customer?.photo, user?.data?.foto, isSuccessUpload]
  );

  const resolvedPhotoSrc = useMemo(() => {
    if (!photoFile || !urlLogo) return logoSiberat;
    return `${urlLogo.replace(/\/$/, '')}/${photoFile}`;
  }, [photoFile, urlLogo]);

  const [logoSrc, setLogoSrc] = useState(resolvedPhotoSrc);

  useEffect(() => {
    setLogoSrc(resolvedPhotoSrc);
  }, [resolvedPhotoSrc]);

  // Mobile: drawer tertutup default; tutup saat resize ke mobile
  useEffect(() => {
    const mq = window.matchMedia(MOBILE_MQ);
    const syncMobile = () => {
      if (mq.matches) dispatch(wrap());
    };
    syncMobile();
    mq.addEventListener('change', syncMobile);
    return () => mq.removeEventListener('change', syncMobile);
  }, [dispatch]);

  // Tutup drawer mobile setelah pindah halaman
  useEffect(() => {
    if (window.matchMedia(MOBILE_MQ).matches) {
      dispatch(wrap());
    }
  }, [pathname, dispatch]);

  useEffect(() => {
    if (!window.matchMedia(MOBILE_MQ).matches) return undefined;
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  const onLogout = () => {
    dispatch(reset());
    dispatch(logout());
    dispatch(resetProfile());
    dispatch(setPic(null));
    toast.success('Signed out successfully');
    navigate('/');
  };

  const closeMobile = () => {
    if (window.matchMedia(MOBILE_MQ).matches) {
      dispatch(wrap());
    }
  };

  return (
    <>
      {open ? (
        <button
          type='button'
          className='fixed inset-0 z-30 bg-black/40 sm:hidden border-0 p-0 cursor-pointer'
          aria-label='Tutup menu'
          onClick={closeMobile}
        />
      ) : null}

      <aside
        id='sidebar'
        className={`fixed sm:relative top-16 bottom-0 sm:top-0 left-0 z-40 sm:z-auto flex flex-col shrink-0 sm:h-full sm:min-h-screen bg-white border-r border-[#E7E7E7] transition-all duration-300 ease-in-out w-60 ${
          open ? 'sm:w-60' : 'sm:w-[72px] mini-sidebar'
        } ${
          open ? 'translate-x-0' : '-translate-x-full sm:translate-x-0'
        }`}>
        <div
          className={`flex flex-col items-center pt-6 pb-4 px-3 border-b border-[#ECEFF3] ${
            open ? '' : 'sm:px-2'
          }`}>
          <img
            {...(logoSrc !== logoSiberat ? { crossOrigin: 'anonymous' } : {})}
            src={logoSrc}
            alt={user?.data?.name || 'Siberat'}
            onError={() => {
              if (logoSrc !== logoSiberat) setLogoSrc(logoSiberat);
            }}
            className={`object-contain ${
              open ? 'w-[88px]' : 'sm:w-10 w-[88px]'
            }`}
          />
          <span
            className={`mt-3 text-sm font-semibold text-[#1B2124] text-center leading-snug px-1 ${
              open ? '' : 'sm:hidden'
            }`}>
            {user.data.name}
          </span>
        </div>

        <nav className='flex-1 w-full overflow-y-auto py-4 px-3'>
          <ul className='flex flex-col gap-1'>
            {navItems.map(item => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  onClick={closeMobile}
                  title={!open ? item.label : undefined}
                  className={({ isActive }) => {
                    const active =
                      isActive ||
                      (item.to === '/dashboard' && splitLocation[1] === '');
                    return `flex items-center gap-3 rounded-lg text-sm no-underline transition-colors px-3 py-2.5 ${
                      open ? '' : 'sm:justify-center sm:px-2'
                    } ${
                      active
                        ? 'bg-[#FFF6D8] text-[#1B2124] font-semibold'
                        : 'text-[#4F4F4F] font-medium hover:bg-[#F6F6F6]'
                    }`;
                  }}>
                  {({ isActive }) => {
                    const active =
                      isActive ||
                      (item.to === '/dashboard' && splitLocation[1] === '');
                    return (
                      <>
                        <Icon
                          icon={item.icon}
                          width={22}
                          height={22}
                          className='sidebar-nav__icon'
                          style={{
                            color: active ? '#1B2124' : '#6B7280',
                            flexShrink: 0
                          }}
                        />
                        <span className={open ? '' : 'sm:hidden'}>
                          {item.label}
                        </span>
                      </>
                    );
                  }}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <div className='mt-auto p-3 border-t border-[#ECEFF3]'>
          <button
            type='button'
            onClick={onLogout}
            title={!open ? 'Log out' : undefined}
            className={`w-full flex items-center gap-3 rounded-lg text-sm font-medium text-[#912018] hover:bg-[#FEF3F2] transition-colors px-3 py-2.5 ${
              open ? '' : 'sm:justify-center sm:px-2'
            }`}>
            <Icon
              icon='solar:logout-2-bold'
              width={22}
              height={22}
              style={{ color: '#912018', flexShrink: 0 }}
            />
            <span className={open ? '' : 'sm:hidden'}>Log out</span>
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
